/**
 * PoolStatusTable Component - Phase 3H-U
 * 
 * Main dashboard table showing user's pools with actionable columns.
 * Replaces ActivePoolsList and MyStatsCard.
 * 
 * Click behaviors:
 * - Pool name → /pools/{id}
 * - Roster → /pools/{id}/gm
 * - Units → /pools/{id}/gm
 * - Rank → /pools/{id}/leaderboard
 * 
 * PHASE 3H-U FIX: Added loading guard to prevent duplicate fetches
 * Following pattern from useLeaderboardPolling.ts (Phase 3H-B)
 */
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../common';
import { Currency } from '../common';
import { EmptyState, LoadingSpinner } from '../common';
import type { PoolMembership, Pool } from '../../types';

const LockClosed = () => (
  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const LockOpen = () => (
  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 019.9-1" />
  </svg>
);


interface PoolEntryState {
  filledSlots: number;
  totalSlots: number;
  units: number;
  isLocked: boolean;
  rank: number | null;
}

interface PoolStatusTableProps {
  memberships: PoolMembership[];
  pools: Pool[];
  loading?: boolean;
  className?: string;
}

export function PoolStatusTable({
  memberships,
  pools,
  loading = false,
  className = '',
}: PoolStatusTableProps): JSX.Element {
  const navigate = useNavigate();
  const [entryStates, setEntryStates] = useState<Record<string, PoolEntryState>>({});
  const [loadingEntries, setLoadingEntries] = useState(true);

  // PHASE 3H-U FIX: Loading guards to prevent duplicate fetches
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const lastPoolIdsRef = useRef<string>('');

  const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

  const fetchEntryStates = useCallback(async () => {
    // Guard: Skip if already loading
    if (isLoadingRef.current) {
      return;
    }

    if (memberships.length === 0) {
      setLoadingEntries(false);
      return;
    }

    const userId = localStorage.getItem('current_user_id');
    if (!userId) {
      setLoadingEntries(false);
      return;
    }

    // Guard: Check if pool IDs changed - only refetch if they did
    const currentPoolIds = memberships.map(m => m.pool_id).sort().join(',');
    if (hasLoadedRef.current && currentPoolIds === lastPoolIdsRef.current) {
      return;
    }

    // Set loading guards
    isLoadingRef.current = true;
    lastPoolIdsRef.current = currentPoolIds;

    const states: Record<string, PoolEntryState> = {};

    await Promise.all(
      memberships.map(async (membership) => {
        const pool = pools.find(p => p.pool_id === membership.pool_id);
        if (!pool) return;

        const poolId = pool.pool_id; // ALWAYS use string pool_id for API calls

        try {
          // Fetch entry data
          const entryRes = await fetch(`${API_BASE}/pools/${poolId}/entry`, {
            headers: { 'X-User-ID': userId }
          });
          
          let filledSlots = 0;
          let totalSlots = 7;
          let units = 0;
          let isLocked = false;

          if (entryRes.ok) {
            const entryData = await entryRes.json();
            if (entryData.entry) {
              filledSlots = entryData.entry.roster?.filter((s: any) => s.player !== null).length || 0;
              totalSlots = entryData.entry.roster?.length || 7;
              units = entryData.entry.totalUnits || 0;
              isLocked = entryData.state === 'LOCKED' || entryData.state === 'COMPLETED';
            }
          }

          // Fetch leaderboard for rank
          let rank: number | null = null;
          try {
            const lbRes = await fetch(`${API_BASE}/pools/${poolId}/leaderboard`);
            if (lbRes.ok) {
              const lbData = await lbRes.json();
              const userEntry = lbData.entries?.find((s: any) => s.user_id === userId);
              rank = userEntry?.rank || null;
            }
          } catch {
            // Leaderboard may not exist yet
          }

          states[membership.pool_id] = { filledSlots, totalSlots, units, isLocked, rank };
        } catch (err) {
          console.error(`Failed to fetch entry for pool ${membership.pool_id}:`, err);
          states[membership.pool_id] = { filledSlots: 0, totalSlots: 7, units: 0, isLocked: false, rank: null };
        }
      })
    );

    setEntryStates(states);
    setLoadingEntries(false);
    hasLoadedRef.current = true;
    isLoadingRef.current = false;
  }, [memberships, pools, API_BASE]);

  useEffect(() => {
    if (pools.length > 0 && memberships.length > 0) {
      fetchEntryStates();
    } else if (pools.length === 0 || memberships.length === 0) {
      setLoadingEntries(false);
    }
  }, [fetchEntryStates, pools.length, memberships.length]);

  const activeItems = memberships
    .map((membership) => {
      const pool = pools.find((p) => p.pool_id === membership.pool_id);
      return { membership, pool };
    })
    .filter(({ pool }) => pool && (pool.status === 'active' || pool.status === 'pending' || pool.status === 'locked'));

  if (loading || loadingEntries) {
    return (
      <Card className={className}>
        <CardHeader><CardTitle>My Pools</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32"><LoadingSpinner size="lg" /></div>
        </CardContent>
      </Card>
    );
  }

  if (activeItems.length === 0) {
    return (
      <Card className={className}>
        <CardHeader><CardTitle>My Pools</CardTitle></CardHeader>
        <CardContent>
          <EmptyState title="No active pools" description="You haven't been assigned to any pools yet" />
        </CardContent>
      </Card>
    );
  }

  const extractWeek = (poolName: string): string => {
    const match = poolName.match(/Week\s*(\d+)/i);
    return match ? match[1] : '-';
  };

  return (
    <Card className={className}>
      <CardHeader><CardTitle>My Pools</CardTitle></CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
             <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Pool</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Current Week</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Roster Status</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Dollars in Play</th>
                <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Current Rank</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Available Balance</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map(({ membership, pool }) => {
                const entryState = entryStates[membership.pool_id] || {
                  filledSlots: 0, totalSlots: 7, units: 0, isLocked: false, rank: null
                };

                return (
                  <tr key={membership.pool_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td 
                      className="py-3 px-4 cursor-pointer hover:text-blue-400"
                      onClick={() => navigate(`/pools/${membership.pool_id}`)}
                    >
                      <span className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1">
                        {pool?.pool_name || membership.pool_id}
                        <span className="text-blue-400">→</span>
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center text-slate-600">
                      {extractWeek(pool?.pool_name || '')}
                    </td>
                    <td 
                      className="py-3 px-2 text-center cursor-pointer hover:text-blue-400"
                      onClick={() => navigate(`/pools/${membership.pool_id}/gm`)}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {entryState.isLocked ? <LockClosed /> : <LockOpen />}
                        <span className="text-slate-900">{entryState.filledSlots}/{entryState.totalSlots}</span>
                      </div>
                    </td>
                    <td 
                      className="py-3 px-2 text-center cursor-pointer hover:text-blue-400"
                      onClick={() => navigate(`/pools/${membership.pool_id}/gm`)}
                    >
                      <span className="text-slate-900 font-mono">{entryState.units}</span>
                    </td>
                    <td 
                      className="py-3 px-2 text-center cursor-pointer hover:text-blue-400"
                      onClick={() => navigate(`/pools/${membership.pool_id}/leaderboard`)}
                    >
                      <span className="text-slate-900">{entryState.rank ? `#${entryState.rank}` : '-'}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Currency value={membership.current_balance} className="!text-slate-800 font-bold" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default PoolStatusTable;
